import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Search, Plus, Eye, Pencil, FileSpreadsheet, FileText,
  Briefcase, Tags, ClipboardList, BarChart3, TrendingUp,
  DollarSign, Package, Users, Calendar, X, Upload,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { usePermission } from "@/hooks/usePermission";
import { toast } from "sonner";
import OTACCatalogsTab from "@/pages/OTACCatalogs";
import { CotizacionesACListTab } from "@/pages/CotizacionesACList";

const ESTATUS_OPTS = ["Pendiente", "En proceso", "Terminado", "Entregado", "Cancelado"];

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

function fmtNum(v) {
  return new Intl.NumberFormat("es-MX").format(v || 0);
}

const CHART_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#6366f1", "#ec4899", "#14b8a6"];

const STATUS_COLORS = {
  Pendiente: "#f59e0b",
  "En proceso": "#3b82f6",
  Terminado: "#10b981",
  Entregado: "#8b5cf6",
  Cancelado: "#ef4444",
};

/* ─── Date presets ─── */
function getPresetDates(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case "este_mes": {
      const from = new Date(y, m, 1);
      return { desde: from.toISOString().slice(0, 10), hasta: now.toISOString().slice(0, 10) };
    }
    case "mes_pasado": {
      const from = new Date(y, m - 1, 1);
      const to = new Date(y, m, 0);
      return { desde: from.toISOString().slice(0, 10), hasta: to.toISOString().slice(0, 10) };
    }
    case "trimestre": {
      const from = new Date(y, m - 2, 1);
      return { desde: from.toISOString().slice(0, 10), hasta: now.toISOString().slice(0, 10) };
    }
    case "este_anio": {
      return { desde: `${y}-01-01`, hasta: now.toISOString().slice(0, 10) };
    }
    default:
      return { desde: "", hasta: "" };
  }
}

/* ═══════════════════════════════════════════════════════════════
   DASHBOARD TAB
   ═══════════════════════════════════════════════════════════════ */
function OTACDashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["otac-stats"],
    queryFn: async () => (await api.get("/ots-comercial/stats")).data,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Skeleton className="h-72 rounded-lg" />
          <Skeleton className="h-72 rounded-lg" />
        </div>
      </div>
    );
  }

  const kpis = stats?.kpis || {};
  const porEstatus = stats?.por_estatus || [];
  const porMes = stats?.por_mes || [];
  const topClientes = stats?.top_clientes || [];
  const topTipos = stats?.top_tipos || [];
  const recientes = stats?.recientes || [];

  const pieData = porEstatus.map(s => ({
    name: s.estatus,
    value: s.count,
    fill: STATUS_COLORS[s.estatus] || "#94a3b8",
  }));

  const mesLabels = { "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun", "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic" };
  const barData = porMes.map(m => ({
    mes: mesLabels[m.mes.split("-")[1]] || m.mes,
    costo: m.costo,
    ots: m.ots,
    qty: m.qty,
  }));

  return (
    <div className="space-y-5" data-testid="otac-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-card border-border" data-testid="otac-kpi-total-ots">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <ClipboardList className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide">Total OTs</span>
            </div>
            <p className="text-2xl font-bold tabular-nums">{fmtNum(kpis.total_ots)}</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border border-l-4 border-l-amber-500/60" data-testid="otac-kpi-costo-maquila">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4 text-amber-400" />
              <span className="text-xs uppercase tracking-wide">Costo Maquila</span>
            </div>
            <p className="text-2xl font-bold tabular-nums text-amber-400">{fmtMoney(kpis.total_costo_maquila)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border border-l-4 border-l-emerald-500/60" data-testid="otac-kpi-costo-comercial">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4 text-emerald-400" />
              <span className="text-xs uppercase tracking-wide">Costo Comercial</span>
            </div>
            <p className="text-2xl font-bold tabular-nums text-emerald-400">{fmtMoney(kpis.total_costo_comercial)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border" data-testid="otac-kpi-total-qty">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Package className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wide">Total Piezas</span>
            </div>
            <p className="text-2xl font-bold tabular-nums">{fmtNum(kpis.total_qty)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* OTs por Estatus - Pie */}
        <Card className="bg-card border-border" data-testid="otac-chart-estatus">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">OTs por Estatus</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} label={({ name, value }) => `${name} (${value})`}>
                    {pieData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [v, "OTs"]} contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">Sin datos</div>
            )}
          </CardContent>
        </Card>

        {/* Costo por Mes - Bar */}
        <Card className="bg-card border-border" data-testid="otac-chart-mes">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Costo por Mes</CardTitle>
          </CardHeader>
          <CardContent>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [fmtMoney(v), "Costo"]} contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="costo" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">Sin datos</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Top Clientes + Top Tipos + Recientes */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Top Clientes */}
        <Card className="bg-card border-border" data-testid="otac-top-clientes">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Users className="h-4 w-4" /> Top Clientes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {topClientes.length === 0 && <p className="text-sm text-muted-foreground p-4">Sin datos</p>}
              {topClientes.map((c, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5">
                  <div>
                    <p className="text-sm font-medium">{c.cliente}</p>
                    <p className="text-xs text-muted-foreground">{c.ots} OTs · {fmtNum(c.qty)} pzs</p>
                  </div>
                  <span className="text-sm font-semibold text-primary tabular-nums">{fmtMoney(c.costo)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Tipos de Trabajo */}
        <Card className="bg-card border-border" data-testid="otac-top-tipos">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Briefcase className="h-4 w-4" /> Tipos de Trabajo
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {topTipos.length === 0 && <p className="text-sm text-muted-foreground p-4">Sin datos</p>}
              {topTipos.map((t, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5">
                  <div>
                    <p className="text-sm font-medium">{t.tipo}</p>
                    <p className="text-xs text-muted-foreground">{t.count} OTs</p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums">{fmtMoney(t.costo)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recientes */}
        <Card className="bg-card border-border" data-testid="otac-recientes">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Calendar className="h-4 w-4" /> OTs Recientes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {recientes.length === 0 && <p className="text-sm text-muted-foreground p-4">Sin datos</p>}
              {recientes.map((r, i) => (
                <Link key={i} to={`/ots-comercial/${r.id}`} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors">
                  <div>
                    <p className="text-sm font-mono font-semibold text-primary">{r.folio}</p>
                    <p className="text-xs text-muted-foreground">{r.cliente} · {r.created_at?.slice(0, 10)}</p>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={r.estatus} />
                    <p className="text-xs tabular-nums mt-0.5">{fmtMoney(r.total_costo)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */
export default function OTACListPage() {
  const [search, setSearch] = useState("");
  const [estatus, setEstatus] = useState("all");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const canCreate = usePermission("otac_create");
  const canEdit = usePermission("otac_edit");
  const canExport = usePermission("otac_export");

  // Support ?tab=cotizador from navigation
  const searchParams = new URLSearchParams(window.location.search);
  const initialTab = searchParams.get("tab") || "dashboard";
  const [activeTab, setActiveTab] = React.useState(initialTab);

  const { data: ots = [], isLoading } = useQuery({
    queryKey: ["otac-list", search, fechaDesde, fechaHasta],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      return (await api.get("/ots-comercial", { params })).data;
    },
  });

  const filtered = useMemo(() => {
    return ots.filter((o) => {
      if (estatus !== "all" && o.estatus !== estatus) return false;
      return true;
    });
  }, [ots, estatus]);

  const applyPreset = (preset) => {
    const { desde, hasta } = getPresetDates(preset);
    setFechaDesde(desde);
    setFechaHasta(hasta);
  };

  const clearDates = () => {
    setFechaDesde("");
    setFechaHasta("");
  };

  const hasDateFilter = fechaDesde || fechaHasta;

  const downloadFile = async (id, kind, folio) => {
    try {
      const res = await api.get(`/ots-comercial/${id}/${kind}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OT_AC_${folio}.${kind === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Descarga ${kind.toUpperCase()} iniciada`);
    } catch (e) {
      toast.error("Error al descargar");
    }
  };

  return (
    <div className="space-y-4" data-testid="otac-list-page">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">OTs Área Comercial</h1>
        <p className="text-sm text-muted-foreground">Ordenes de trabajo del área comercial con folios OT-AC.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="otac-main-tab-dashboard">
            <BarChart3 className="h-4 w-4 mr-1.5" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="lista" data-testid="otac-main-tab-lista">
            <ClipboardList className="h-4 w-4 mr-1.5" /> OTs
          </TabsTrigger>
          <TabsTrigger value="cotizador" data-testid="otac-main-tab-cotizador">
            <DollarSign className="h-4 w-4 mr-1.5" /> Cotizador
          </TabsTrigger>
          <TabsTrigger value="catalogos" data-testid="otac-main-tab-catalogos">
            <Tags className="h-4 w-4 mr-1.5" /> Catálogos
          </TabsTrigger>
        </TabsList>

        {/* ── Dashboard Tab ── */}
        <TabsContent value="dashboard" className="mt-3">
          <OTACDashboard />
        </TabsContent>

        {/* ── Lista Tab ── */}
        <TabsContent value="lista" className="mt-3 space-y-4">
          {/* Header + botones */}
          <div className="flex justify-end gap-2">
            {canCreate && (
              <Button variant="outline" asChild data-testid="otac-upload-button">
                <Link to="/ots-comercial/upload"><Upload className="h-4 w-4 mr-1" /> Cargar Excel</Link>
              </Button>
            )}
            {canCreate && (
              <Button asChild data-testid="otac-create-button">
                <Link to="/ots-comercial/new"><Plus className="h-4 w-4 mr-1" /> Nueva OT Comercial</Link>
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar folio, folio comercial, cliente, SKU..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="otac-search-input" />
              </div>
              <Select value={estatus} onValueChange={setEstatus}>
                <SelectTrigger className="w-40" data-testid="otac-filter-estatus"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {ESTATUS_OPTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Badge variant="outline" className="py-1 px-3 font-mono tabular-nums" data-testid="otac-count-badge">
                {filtered.length} OTs
              </Badge>
            </div>

            {/* Date Filters */}
            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>Filtrar por fecha:</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyPreset("este_mes")} data-testid="otac-date-este-mes">Este mes</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyPreset("mes_pasado")} data-testid="otac-date-mes-pasado">Mes pasado</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyPreset("trimestre")} data-testid="otac-date-trimestre">Último trimestre</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyPreset("este_anio")} data-testid="otac-date-este-anio">Este año</Button>
              </div>
              <div className="flex items-center gap-1.5">
                <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="h-7 text-xs w-36" data-testid="otac-date-desde" />
                <span className="text-xs text-muted-foreground">a</span>
                <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="h-7 text-xs w-36" data-testid="otac-date-hasta" />
              </div>
              {hasDateFilter && (
                <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={clearDates} data-testid="otac-date-clear">
                  <X className="h-3 w-3 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </div>

          {/* Table */}
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-2.5 px-3">Folio</th>
                      <th className="py-2.5 px-3">Folio Comercial</th>
                      <th className="py-2.5 px-3">Cliente</th>
                      <th className="py-2.5 px-3">Tipo Trabajo</th>
                      <th className="py-2.5 px-3 text-right">QTY</th>
                      <th className="py-2.5 px-3 text-right">C. Maquila</th>
                      <th className="py-2.5 px-3 text-right">C. Comercial</th>
                      <th className="py-2.5 px-3">Estatus</th>
                      <th className="py-2.5 px-3 w-36">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && [1,2,3].map(i => (
                      <tr key={i} className="border-t border-border/60">
                        {Array(9).fill(0).map((_, j) => <td key={j} className="py-2.5 px-3"><Skeleton className="h-4 w-full" /></td>)}
                      </tr>
                    ))}
                    {!isLoading && filtered.map((o) => (
                      <tr key={o.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`otac-row-${o.id}`}>
                        <td className="py-2.5 px-3">
                          <Link to={`/ots-comercial/${o.id}`} className="font-mono font-semibold text-primary hover:underline" data-testid={`otac-link-${o.folio}`}>
                            {o.folio}
                          </Link>
                        </td>
                        <td className="py-2.5 px-3 text-xs text-muted-foreground">{o.folio_comercial || "-"}</td>
                        <td className="py-2.5 px-3 font-medium">{o.cliente}</td>
                        <td className="py-2.5 px-3 text-xs">{o.tipo_trabajo || "-"}</td>
                        <td className="py-2.5 px-3 text-right font-mono tabular-nums">{o.total_qty || 0}</td>
                        <td className="py-2.5 px-3 text-right font-mono tabular-nums text-amber-400">{fmtMoney(o.costo_maquila)}</td>
                        <td className="py-2.5 px-3 text-right font-mono tabular-nums text-emerald-400">{fmtMoney(o.costo_comercial)}</td>
                        <td className="py-2.5 px-3"><StatusBadge status={o.estatus} /></td>
                        <td className="py-2.5 px-3">
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" asChild data-testid={`otac-view-${o.id}`}>
                              <Link to={`/ots-comercial/${o.id}`}><Eye className="h-3.5 w-3.5" /></Link>
                            </Button>
                            {canEdit && (
                              <Button size="icon" variant="ghost" className="h-7 w-7" asChild data-testid={`otac-edit-${o.id}`}>
                                <Link to={`/ots-comercial/${o.id}/edit`}><Pencil className="h-3.5 w-3.5" /></Link>
                              </Button>
                            )}
                            {canExport && (
                              <>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => downloadFile(o.id, "excel", o.folio)} data-testid={`otac-excel-${o.id}`}>
                                  <FileSpreadsheet className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => downloadFile(o.id, "pdf", o.folio)} data-testid={`otac-pdf-${o.id}`}>
                                  <FileText className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!isLoading && filtered.length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-12 text-center text-muted-foreground">
                          <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-40" />
                          {search || hasDateFilter ? "Sin resultados para los filtros seleccionados" : "Sin OTs Comerciales. Crea la primera."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Cotizador Tab ── */}
        <TabsContent value="cotizador" className="mt-3">
          <CotizacionesACListTab />
        </TabsContent>

        {/* ── Catálogos Tab ── */}
        <TabsContent value="catalogos" className="mt-3">
          <OTACCatalogsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
